import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import videojs from 'video.js'
import Player from 'video.js/dist/types/player'
import 'video.js/dist/video-js.css'
import styles from './VideoPlayer.module.less'
import {
    BackwardButton,
    CopyVideoCurrentTime,
    ForwardButton,
    SegmentTimeWithMilliseconds,
    SegmentRangeControl,
} from './VideoPlayerCustomComponents'
import { formatTimeWithMilliseconds } from '../../common/common.utils'

export type SegmentedVideoPlayerPropsType = {
    url: string
    id: string
}

export type SegmentedVideoPlayerHandle = {
    playSegment: (startTime: number, endTime: number) => void
    setSegment: (startTime: number, endTime: number) => void
    seekToTime: (seconds: number) => void
    getCurrentSegment: () => [number, number]
}

const DEFAULT_FPS = 30

export const SegmentedVideoPlayer = forwardRef<
    SegmentedVideoPlayerHandle,
    SegmentedVideoPlayerPropsType
>((props, ref) => {
    const videoRef = useRef<HTMLDivElement | null>(null)
    const playerRef = useRef<Player | null>(null)
    const segmentRef = useRef<{ startTime: number; endTime: number } | null>(null)
    const isPlayingSegmentRef = useRef<boolean>(false)
    const loadedMetadataHandlerRef = useRef<(() => void) | null>(null)
    const [segmentDisplay, setSegmentDisplay] = useState<{
        startTime: number
        endTime: number
    } | null>(null)
    const [frameRate, setFrameRate] = useState<number>(DEFAULT_FPS)

    const getFrameNumber = (timeInSeconds: number): number => {
        return Math.floor(timeInSeconds * frameRate)
    }

    const isValidSegment = (segment: { startTime: number; endTime: number } | null): boolean => {
        return segment !== null && isFinite(segment.startTime) && isFinite(segment.endTime)
    }

    const options = useMemo(
        () => ({
            controls: true,
            experimentalSvgIcons: true,
            playbackRates: [0.15, 0.25, 0.5, 1, 1.5, 2],
            controlBar: {
                alwaysVisible: true,
                children: [
                    { name: 'customBackwardButton', align: 'left' },
                    { name: 'playToggle', align: 'left' },
                    { name: 'customForwardButton', align: 'left' },
                    { name: 'segmentTimeWithMilliseconds', align: 'left' },
                    { name: 'segmentRangeControl', align: 'left' },
                    { name: 'copyVideoCurrentTime', align: 'right' },
                    { name: 'volumePanel', align: 'right', inline: false },
                    { name: 'playbackRateMenuButton', align: 'right' },
                    { name: 'fullscreenToggle', align: 'right' },
                    { name: 'pictureInPictureToggle', align: 'right' },
                ],
            },
            sources: props.url
                ? [
                      {
                          src: props.url,
                      },
                  ]
                : [],
        }),
        [props.url]
    )

    useEffect(() => {
        if (!playerRef.current) {
            const videoElement = document.createElement('video-js')

            videoElement.classList.add('vjs-big-play-centered')
            videoRef.current!.appendChild(videoElement)

            videojs.registerComponent('segmentTimeWithMilliseconds', SegmentTimeWithMilliseconds)
            videojs.registerComponent('segmentRangeControl', SegmentRangeControl)
            videojs.registerComponent('copyVideoCurrentTime', CopyVideoCurrentTime)
            videojs.registerComponent('customForwardButton', ForwardButton)
            videojs.registerComponent('customBackwardButton', BackwardButton)
            playerRef.current = videojs(videoElement, options, () => {
                videojs.log('player is ready')
                const player = playerRef.current!

                const handleLoadedMetadata = () => {
                    const duration = player.duration() ?? 0
                    const videoElement = player.el().querySelector('video') as HTMLVideoElement

                    if (videoElement) {
                        try {
                            const playbackQuality = videoElement.getVideoPlaybackQuality?.()
                            if (
                                playbackQuality &&
                                playbackQuality.totalVideoFrames &&
                                duration > 0
                            ) {
                                const detectedFps = playbackQuality.totalVideoFrames / duration
                                if (isFinite(detectedFps) && detectedFps > 0 && detectedFps < 120) {
                                    setFrameRate(Math.round(detectedFps))
                                }
                            }
                        } catch (e) {
                            // Could not detect frame rate, using default
                        }
                    }

                    if (isFinite(duration) && !isNaN(duration) && duration > 0) {
                        // @ts-ignore
                        if (!player.segment_) {
                            const initialSegment = { startTime: 0, endTime: duration }
                            // @ts-ignore
                            player.segment_ = initialSegment
                            segmentRef.current = initialSegment
                            setSegmentDisplay(initialSegment)
                            player.trigger('segmentchange')
                        }
                    }
                }

                loadedMetadataHandlerRef.current = handleLoadedMetadata
                player.on('loadedmetadata', handleLoadedMetadata)
            })
        } else {
            const player = playerRef.current
            player.src(options.sources)
        }

        return () => {
            const player = playerRef.current
            if (player && loadedMetadataHandlerRef.current) {
                player.off('loadedmetadata', loadedMetadataHandlerRef.current)
                loadedMetadataHandlerRef.current = null
            }
        }
    }, [options])

    useEffect(() => {
        const player = playerRef.current
        if (!player) return

        const checkAndUpdateDisplay = () => {
            // @ts-ignore
            const segment = player.segment_ || segmentRef.current
            if (isValidSegment(segment)) {
                setSegmentDisplay({ startTime: segment.startTime, endTime: segment.endTime })
            }
        }

        checkAndUpdateDisplay()

        const handleSegmentChange = () => {
            checkAndUpdateDisplay()
        }

        player.on('segmentchange', handleSegmentChange)
        player.on('loadedmetadata', checkAndUpdateDisplay)

        return () => {
            player.off('segmentchange', handleSegmentChange)
            player.off('loadedmetadata', checkAndUpdateDisplay)
        }
    }, [])

    useEffect(() => {
        const player = playerRef.current
        if (!player) return

        const handleTimeUpdate = () => {
            // @ts-ignore
            const segment = player.segment_ || segmentRef.current
            if (!segment) return

            const currentTime = player.currentTime() ?? 0
            const { startTime, endTime } = segment

            if (!isFinite(startTime) || !isFinite(endTime) || isNaN(startTime) || isNaN(endTime)) {
                return
            }

            if (currentTime >= endTime && isFinite(endTime)) {
                try {
                    if (currentTime > endTime) {
                        player.currentTime(endTime)
                    }
                    if (!player.paused()) {
                        player.pause()
                    }
                } catch (error) {
                    // Error handling end time in handleTimeUpdate
                }
            } else if (currentTime < startTime && isFinite(startTime)) {
                try {
                    player.currentTime(startTime)
                } catch (error) {
                    // Error setting currentTime in handleTimeUpdate
                }
            }
        }

        const handlePlay = () => {
            // @ts-ignore
            const segment = player.segment_ || segmentRef.current
            if (!segment) return

            const currentTime = player.currentTime() ?? 0
            const { startTime, endTime } = segment

            if (!isFinite(startTime) || !isFinite(endTime) || isNaN(startTime) || isNaN(endTime)) {
                isPlayingSegmentRef.current = true
                return
            }

            if ((currentTime < startTime || currentTime >= endTime) && isFinite(startTime)) {
                try {
                    player.currentTime(startTime)
                } catch (error) {
                    // Error setting currentTime in handlePlay
                }
            }
            isPlayingSegmentRef.current = true
        }

        const handleSeeking = () => {
            // @ts-ignore
            const segment = player.segment_ || segmentRef.current
            if (!segment) return

            const currentTime = player.currentTime() ?? 0
            const { startTime, endTime } = segment

            if (!isFinite(startTime) || !isFinite(endTime) || isNaN(startTime) || isNaN(endTime)) {
                return
            }
            if (currentTime < startTime && isFinite(startTime)) {
                try {
                    player.currentTime(startTime)
                } catch (error) {
                    // Error setting currentTime in handleSeeking
                }
            } else if (currentTime > endTime && isFinite(endTime)) {
                try {
                    player.currentTime(endTime)
                } catch (error) {
                    // Error setting currentTime in handleSeeking
                }
            }
        }

        // Listen for segment updates from the range control
        // In video.js, custom event data is passed as additional arguments after the event object
        const handleSegmentUpdate = (
            event: Event,
            data?: { startTime?: number; endTime?: number }
        ) => {
            // Extract data from the second argument (video.js passes custom data as additional args)
            // Also check if data was merged into event object (fallback)
            const eventWithData = event as Event & {
                startTime?: number
                endTime?: number
                detail?: { startTime?: number; endTime?: number }
            }
            const startTime =
                data?.startTime ?? eventWithData.startTime ?? eventWithData.detail?.startTime
            const endTime = data?.endTime ?? eventWithData.endTime ?? eventWithData.detail?.endTime

            if (
                startTime === undefined ||
                endTime === undefined ||
                !isFinite(startTime) ||
                !isFinite(endTime) ||
                isNaN(startTime) ||
                isNaN(endTime)
            ) {
                return
            }

            segmentRef.current = { startTime, endTime }
            // @ts-ignore
            player.segment_ = { startTime, endTime }
            player.trigger('segmentchange')
            setSegmentDisplay({ startTime, endTime })

            // If currently playing and outside new segment, adjust
            const currentTime = player.currentTime() ?? 0
            if (currentTime < startTime && isFinite(startTime)) {
                try {
                    player.currentTime(startTime)
                } catch (error) {
                    // Error setting currentTime in handleSegmentUpdate
                }
            } else if (currentTime > endTime && isFinite(endTime)) {
                try {
                    player.currentTime(endTime)
                } catch (error) {
                    // Error setting currentTime in handleSegmentUpdate
                }
            }
        }

        const handleSegmentChange = () => {
            // @ts-ignore
            const segment = player.segment_ || segmentRef.current
            if (isValidSegment(segment)) {
                setSegmentDisplay({ startTime: segment.startTime, endTime: segment.endTime })
            }
        }

        // @ts-ignore
        const existingSegment = player.segment_ || segmentRef.current
        if (isValidSegment(existingSegment)) {
            setSegmentDisplay({
                startTime: existingSegment.startTime,
                endTime: existingSegment.endTime,
            })
        }

        player.on('timeupdate', handleTimeUpdate)
        player.on('play', handlePlay)
        player.on('seeking', handleSeeking)
        player.on('segmentupdate', handleSegmentUpdate)
        player.on('segmentchange', handleSegmentChange)

        return () => {
            player.off('timeupdate', handleTimeUpdate)
            player.off('play', handlePlay)
            player.off('seeking', handleSeeking)
            player.off('segmentupdate', handleSegmentUpdate)
            player.off('segmentchange', handleSegmentChange)
        }
    }, [])

    useEffect(() => {
        return () => {
            const player = playerRef.current
            if (player && !player.isDisposed()) {
                player.dispose()
                playerRef.current = null
            }
        }
    }, [])

    useImperativeHandle(ref, () => ({
        playSegment: (startTime: number, endTime: number) => {
            const player = playerRef.current
            if (!player) {
                return
            }

            const duration = player.duration() ?? 0
            const validStartTime = Math.max(0, Math.min(startTime, duration))
            const validEndTime = Math.max(validStartTime, Math.min(endTime, duration))

            segmentRef.current = { startTime: validStartTime, endTime: validEndTime }
            // @ts-ignore
            player.segment_ = { startTime: validStartTime, endTime: validEndTime }
            player.trigger('segmentchange')
            player.currentTime(validStartTime)
            isPlayingSegmentRef.current = true
            const playPromise = player.play()
            if (playPromise !== undefined) {
                playPromise.catch(() => {
                    isPlayingSegmentRef.current = false
                })
            }
        },

        setSegment: (startTime: number, endTime: number) => {
            const player = playerRef.current
            if (!player) {
                return
            }

            const duration = player.duration() ?? 0
            const validStartTime = Math.max(0, Math.min(startTime, duration))
            const validEndTime = Math.max(validStartTime, Math.min(endTime, duration))

            segmentRef.current = { startTime: validStartTime, endTime: validEndTime }
            // @ts-ignore
            player.segment_ = { startTime: validStartTime, endTime: validEndTime }
            player.trigger('segmentchange')
            isPlayingSegmentRef.current = false
        },

        seekToTime: (seconds: number) => {
            const player = playerRef.current
            if (!player) {
                return
            }

            const duration = player.duration() ?? 0
            // @ts-ignore
            const segment = player.segment_ || segmentRef.current

            let validTime = Math.max(0, Math.min(seconds, duration))

            // Clamp to segment boundaries if segment is set
            if (segment) {
                validTime = Math.max(segment.startTime, Math.min(validTime, segment.endTime))
            }

            player.currentTime(validTime)
            isPlayingSegmentRef.current = false
            if (segmentRef.current) {
                // @ts-ignore
                player.segment_ = segmentRef.current
                player.trigger('segmentchange')
            }
        },

        getCurrentSegment: (): [number, number] => {
            if (!segmentRef.current) {
                return [0, 0]
            }
            return [segmentRef.current.startTime, segmentRef.current.endTime]
        },
    }))

    return (
        <div className={styles['video-player']} id={props.id}>
            <div data-vjs-player>
                <div ref={videoRef} />
            </div>
            {segmentDisplay && (
                <div className={styles['segment-display']}>
                    <div className={styles['segment-display-item-container']}>
                        <div className={styles['segment-display-item']}>
                            <span className={styles['segment-label']}>Start Frame:</span>
                            <span className={styles['segment-value']}>
                                [ {getFrameNumber(segmentDisplay.startTime)} ]
                            </span>
                        </div>
                        <div className={styles['segment-display-item']}>
                            <span className={styles['segment-label']}>End Frame:</span>
                            <span className={styles['segment-value']}>
                                [ {getFrameNumber(segmentDisplay.endTime)} ]
                            </span>
                        </div>
                    </div>
                    <div className={styles['segment-display-item-container']}>
                        <div className={styles['segment-display-item']}>
                            <span className={styles['segment-label']}>Start Time:</span>
                            <span className={styles['segment-value']}>
                                [ {formatTimeWithMilliseconds(segmentDisplay.startTime)} ]
                            </span>
                        </div>
                        <div className={styles['segment-display-item']}>
                            <span className={styles['segment-label']}>End Time:</span>
                            <span className={styles['segment-value']}>
                                [ {formatTimeWithMilliseconds(segmentDisplay.endTime)} ]
                            </span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
})

SegmentedVideoPlayer.displayName = 'SegmentedVideoPlayer'
